import React, { useState, useEffect } from 'react';
import { useTranslation } from 'utils/i18n';

// Import extracted logic
import translations from './translations';
import { useBlogUI } from './logic/ui';
import { loadAllBlogs } from './logic/api';
import { StaticPageShell, PageLoadingState } from 'feSharedIndex';

/**
 * Blog Component
 * Displays blog articles and content
 */
const Blog = () => {
  const { language } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useBlogUI(); // Initialize UI logic

  // Load blogs on mount and when language changes
  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const result = await loadAllBlogs(language);
        if (result.success) {
          setBlogs(result.data || []);
        }
      } catch (error) {
        console.error('Error loading blogs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [language]);

  // Get page-specific translations
  const t = (key, defaultValue = '') => {
    const pageTranslations = translations[language] || translations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get category label
  const getCategoryLabel = (category) => {
    const categoryMap = {
      'technology': t('articles.categories.technology', 'Technology'),
      'privacy': t('articles.categories.privacy', 'Privacy'),
      'success-story': t('articles.categories.successStory', 'Success Story'),
      'trends': t('articles.categories.trends', 'Trends'),
      'business': t('articles.categories.business', 'Business'),
      'design': t('articles.categories.design', 'Design')
    };
    return categoryMap[category] || category;
  };

  return (
    <StaticPageShell
      title={t('title', 'Blog')}
      lead={t('subtitle', 'Latest articles, insights and updates')}
      contentClassName="static-page-content"
    >
      <section className="content-section">
        <h2>{t('welcome.title', 'Welcome to our Blog')}</h2>
        <p>
          {t('welcome.description', 'Stay updated with the latest news, insights, and updates from B-esper.')}
        </p>
      </section>

      <section className="content-section">
        <h2>{t('articles.title', 'Latest Articles')}</h2>
        {loading ? (
          <PageLoadingState message={t('loading', 'Loading articles...')} />
        ) : blogs.length === 0 ? (
            <p>{t('noArticles', 'No articles found.')}</p>
          ) : (
            <div className="blog-articles">
              {blogs.map((blog, index) => (
                <article 
                  key={blog.slug} 
                  className={`blog-article ${blog.featured ? 'featured' : ''}`}
                >
                  {blog.image && (
                    <div className="article-image">
                      <img src={blog.image} alt={blog.title} />
                      <span className="article-category">{getCategoryLabel(blog.category)}</span>
                    </div>
                  )}
                  <div className="article-content">
                    <div className="article-meta">
                      <span className="article-date">{formatDate(blog.date)}</span>
                      {blog.author && (
                        <span className="article-author">{blog.author}</span>
                      )}
                    </div>
                    <h3>{blog.title}</h3>
                    <p>{blog.excerpt || blog.description}</p>
                    {blog.tags && blog.tags.length > 0 && (
                      <div className="article-tags">
                        {blog.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    <a href={`/blog/${blog.slug}`} className="read-more">{t('articles.readMore', 'Read More')}</a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

      <section className="content-section">
        <h2>{t('categories.title', 'Categories')}</h2>
          <div className="blog-categories">
            <div className="category-card">
              <h3>{t('categories.items.technology.title', 'Technology')}</h3>
              <p>{t('categories.items.technology.description', 'Deep-dives into AI technologies, architecture, and development')}</p>
              <a href="/blog/category/technologie" className="category-link">{t('categories.items.technology.articleCount', '12 Articles')}</a>
            </div>

            <div className="category-card">
              <h3>{t('categories.items.bestPractices.title', 'Best Practices')}</h3>
              <p>{t('categories.items.bestPractices.description', 'Proven tips for successful chatbot implementations')}</p>
              <a href="/blog/category/best-practices" className="category-link">{t('categories.items.bestPractices.articleCount', '8 Articles')}</a>
            </div>

            <div className="category-card">
              <h3>{t('categories.items.successStories.title', 'Success Stories')}</h3>
              <p>{t('categories.items.successStories.description', 'How our customers achieved their goals with B-esper')}</p>
              <a href="/blog/category/erfolgsgeschichten" className="category-link">{t('categories.items.successStories.articleCount', '6 Articles')}</a>
            </div>

            <div className="category-card">
              <h3>{t('categories.items.trends.title', 'Trends & Insights')}</h3>
              <p>{t('categories.items.trends.description', 'Market analyses and future outlooks for the AI industry')}</p>
              <a href="/blog/category/trends" className="category-link">{t('categories.items.trends.articleCount', '10 Articles')}</a>
            </div>

            <div className="category-card">
              <h3>{t('categories.items.privacy.title', 'Privacy')}</h3>
              <p>{t('categories.items.privacy.description', 'GDPR, compliance, and legally compliant AI applications')}</p>
              <a href="/blog/category/datenschutz" className="category-link">{t('categories.items.privacy.articleCount', '5 Articles')}</a>
            </div>

            <div className="category-card">
              <h3>{t('categories.items.business.title', 'Business')}</h3>
              <p>{t('categories.items.business.description', 'ROI, strategies, and business models around chatbots')}</p>
              <a href="/blog/category/business" className="category-link">{t('categories.items.business.articleCount', '7 Articles')}</a>
            </div>
          </div>
        </section>

      <section className="content-section">
        <h2>{t('authors.title', 'Our Authors')}</h2>
          <div className="author-grid">
            <div className="author-card">
              <div className="author-avatar">
                <img src="/images/authors/thomas-mueller.jpg" alt={t('authors.items.thomas.name', 'Dr. Thomas Mueller')} />
              </div>
              <div className="author-info">
                <h3>{t('authors.items.thomas.name', 'Dr. Thomas Mueller')}</h3>
                <p className="author-title">{t('authors.items.thomas.title', 'CEO & Co-Founder')}</p>
                <p className="author-bio">
                  {t('authors.items.thomas.bio', 'AI expert with 15+ years of experience. Writes about technology trends and the future of AI-powered automation.')}
                </p>
                <div className="author-stats">
                  <span>{t('authors.items.thomas.stats.articles', '8 Articles')}</span>
                  <span>•</span>
                  <span>{t('authors.items.thomas.stats.readers', '15.2K Readers')}</span>
                </div>
              </div>
            </div>

            <div className="author-card">
              <div className="author-avatar">
                <img src="/images/authors/anna-fischer.jpg" alt={t('authors.items.anna.name', 'Anna Fischer')} />
              </div>
              <div className="author-info">
                <h3>{t('authors.items.anna.name', 'Anna Fischer')}</h3>
                <p className="author-title">{t('authors.items.anna.title', 'CTO & Co-Founder')}</p>
                <p className="author-bio">
                  {t('authors.items.anna.bio', 'Expert in cloud architecture and scaling. Focus on technical deep-dives and developer-oriented content.')}
                </p>
                <div className="author-stats">
                  <span>{t('authors.items.anna.stats.articles', '6 Articles')}</span>
                  <span>•</span>
                  <span>{t('authors.items.anna.stats.readers', '12.8K Readers')}</span>
                </div>
              </div>
            </div>

            <div className="author-card">
              <div className="author-avatar">
                <img src="/images/authors/sarah-weber.jpg" alt={t('authors.items.sarah.name', 'Sarah Weber')} />
              </div>
              <div className="author-info">
                <h3>{t('authors.items.sarah.name', 'Sarah Weber')}</h3>
                <p className="author-title">{t('authors.items.sarah.title', 'Head of Marketing')}</p>
                <p className="author-bio">
                  {t('authors.items.sarah.bio', 'Specialist in digital marketing and customer experience. Writes about best practices and market insights.')}
                </p>
                <div className="author-stats">
                  <span>{t('authors.items.sarah.stats.articles', '12 Articles')}</span>
                  <span>•</span>
                  <span>{t('authors.items.sarah.stats.readers', '18.5K Readers')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

      <section className="content-section">
        <h2>{t('newsletter.title', 'Subscribe to Blog Newsletter')}</h2>
          <p>
            {t('newsletter.description', 'Don\'t miss any new articles! Get weekly updates with the latest insights delivered to your inbox.')}
          </p>
          <div className="newsletter-form">
            <input 
              type="email" 
              placeholder={t('newsletter.placeholder', 'Your email address')}
              className="newsletter-input"
            />
            <button className="newsletter-button btn btn-primary">
              {t('newsletter.subscribe', 'Subscribe')}
            </button>
          </div>
          <p className="newsletter-note">
            {t('newsletter.note', 'Over 5,000 subscribers trust our newsletter. Cancel anytime, 100% spam-free.')}
          </p>
        </section>

      <section className="content-section">
        <h2>{t('contact.title', 'Guest Posts & Collaborations')}</h2>
          <p>
            {t('contact.description', 'Would you like to write a guest post or have ideas for a collaboration? We look forward to your proposal!')}
          </p>
          <p>
            <strong>{t('contact.label', 'Contact')}:</strong> <a href="mailto:blog@b-esper.com">blog@b-esper.com</a>
          </p>
      </section>
    </StaticPageShell>
  );
};

export default Blog;
